import { Icon } from '@ps/isp-web-component-library';
import { ChevronMediumDown } from '@ps/isp-web-component-library/icons/ChevronMediumDown';
import { Size } from '@ps/web-core-enums';
import { mapDatePickerLocale, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/l10n';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect';
import React from 'react';
import Flatpickr from 'react-flatpickr';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import styles from './DatePicker.scss';

interface Props {
  defaultDate?: Date;
  altFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  value?: Date;
  onChange?(dates: Date[]): void;
  hasMonthSelector?: boolean;
  isDisabledMobile?: boolean;
  formatDate?(formattedDate: string): string;
  calendarPosition?: 'left' | 'right';
}

const DatePicker: React.FC<Props> = ({
  defaultDate = Date.now(),
  altFormat = 'D, J M Y',
  minDate,
  maxDate = Date.now(),
  value = Date.now(),
  onChange = () => undefined,
  hasMonthSelector = false,
  isDisabledMobile = false,
  formatDate,
  calendarPosition = 'left'
}) => {
  const locale = useSelector(select.locale.get) as unknown;

  const { greaterThan } = useScreenSize();
  const isMobile = useSelector(select.device.isMobile);

  const isMobileDevice = isMobile || !greaterThan(Size.Small);

  const options: flatpickr.Options.Options = {
    allowInput: false,
    altFormat,
    altInput: true,
    altInputClass: classNames(styles.datePicker, { [styles.monthPicker]: hasMonthSelector }),
    defaultDate,
    disableMobile: isDisabledMobile,
    locale: mapDatePickerLocale(locale) as flatpickr.CustomLocale,
    maxDate,
    minDate,
    onChange,
    static: true
  };

  if (hasMonthSelector) {
    options.plugins = [
      monthSelectPlugin({
        shorthand: true,
        altFormat: 'M Y'
      })
    ];
  }

  if (formatDate) {
    options.formatDate = (date: Date, format: string, locale: flatpickr.Locale) =>
      // @ts-ignore incomplete types - https://github.com/flatpickr/flatpickr/commit/0dcdd547c2b51489626e293f8e6d737de2a8d9e4
      formatDate(flatpickr.formatDate(date, format, locale));
  }

  return (
    <div className={classNames(styles.flatpickrContainer, styles[calendarPosition])}>
      <Flatpickr options={options} value={value} />
      {(isDisabledMobile && isMobileDevice) || greaterThan(Size.Small) ? (
        <Icon
          icon={ChevronMediumDown}
          size="x1"
          className={classNames(styles.icon, {
            [styles.iconOpen]: false,
            [styles.monthPicker]: hasMonthSelector
          })}
        />
      ) : null}
    </div>
  );
};

export { DatePicker };
